import {useShopId} from '@aboutyou/config';
import {
  GrpcResponseError,
  useGrpcRequestMutation,
  useQueryClient,
} from '@aboutyou/data-fetcher';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {WishlistState} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/wishlist.types';
import {WishlistPageService_GetWishlistPageLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/service.grpc.lazy';
import {GetWishlistPageResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/service.types';
import {
  WishlistStreamItem,
  WishlistStreamItem_Product,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/stream.types';
import {
  WishlistService_AddProductLazy,
  WishlistService_RemoveProductLazy,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/service.grpc.lazy';
// eslint-disable-next-line monorepo/no-internal-import
import {useReductionsStateRequestParameter} from '@aboutyou/logic-components/src/features/ReductionsState/useReductionsStateRequestParameter';
// eslint-disable-next-line monorepo/no-internal-import
import {useTargetGroupIdentifier} from '@aboutyou/logic-components/src/shared-deprecated/useTargetGroupIdentifier';
import {Add2WishlistEvent, Log, RemoveFromWishlistEvent} from '@aboutyou/mlm';
import {promiseWithResolver, startSpan, yieldToMain} from '@aboutyou/utils';
import {useCallback} from 'react';
import {useFullSession} from '../userStore/useSession';
import {MUTATION_QUEUE} from './WishlistQueue';
import {useWishlistMinimalState} from './useWishlistState';
import {useGetProductIsInWishlist} from './wishlist.selector';

export enum UseToggleProductInWishlistResults {
  REMOVED = 'REMOVED',
  ADDED = 'ADDED',
}

type WishlistProductItem = {
  streamPosition: number;
  type: {
    $case: 'product';
    product: WishlistStreamItem_Product;
  };
};

const isProduct = (item: WishlistStreamItem): item is WishlistProductItem => {
  return item.type?.$case === 'product';
};

const useRemoveProductFromWishlistCache = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (productId: number) => {
      const wishlistPages = queryClient.getQueriesData(
        WishlistPageService_GetWishlistPageLazy.method,
      );

      // Iterate through all matching queries and update their cache
      wishlistPages.forEach(([queryKey, data]) => {
        if (!data || typeof data !== 'object') {
          return;
        }

        const pageData = data as GetWishlistPageResponse;

        if (!pageData.allItemsTab) {
          return;
        }

        let itemWasRemovedFromAllItems = false;
        let itemWasRemovedFromDiscountedItems = false;

        // Update allItemsTab
        const updatedData: GetWishlistPageResponse = {
          ...pageData,
          allItemsTab: {
            ...pageData.allItemsTab,
            items: pageData.allItemsTab.items.filter(item => {
              if (!isProduct(item)) {
                return true;
              }
              const shouldKeep =
                productId !== item.type.product.product.productId;
              if (!shouldKeep) {
                itemWasRemovedFromAllItems = true;
              }
              return shouldKeep;
            }),
            productCount: itemWasRemovedFromAllItems
              ? pageData.allItemsTab.productCount - 1
              : pageData.allItemsTab.productCount,
          },
        };

        // Update discountedItemsTab if it exists
        if (pageData.discountedItemsTab) {
          updatedData.discountedItemsTab = {
            ...pageData.discountedItemsTab,
            items: pageData.discountedItemsTab.items.filter(item => {
              if (!isProduct(item)) {
                return true;
              }
              const shouldKeep =
                productId !== item.type.product.product.productId;
              if (!shouldKeep) {
                itemWasRemovedFromDiscountedItems = true;
              }
              return shouldKeep;
            }),
            productCount: itemWasRemovedFromDiscountedItems
              ? pageData.discountedItemsTab.productCount - 1
              : pageData.discountedItemsTab.productCount,
          };
        }

        // Update the query cache with the filtered data
        queryClient.setQueryData(queryKey[0] as string, updatedData);
      });
    },
    [queryClient],
  );
};

export const useToggleProductInWishlist = ({
  tracker,
  onError,
}: {
  tracker: (action: 'add-to-wishlist' | 'removed-from-wishlist') => void;
  /**
   * @deprecated
   */
  onError?: (err?: string) => void;
}) => {
  const getProductIsInWishlist = useGetProductIsInWishlist();

  const addProductToWishlist = useAddProductToWishlistMutation();

  const removeProductFromWishlistCache = useRemoveProductFromWishlistCache();
  const removeProductFromWishlist = useRemoveProductFromWishlistMutation();

  const wishlistTracker = tracker;

  return useCallback(
    async ({
      productId,
      onAddSuccess,
    }: {
      productId: Product['id'];
      onAddSuccess?: (productId: number) => void;
    }) => {
      const exists = getProductIsInWishlist({productId});
      await yieldToMain();

      if (exists) {
        await yieldToMain();
        await removeProductFromWishlist.mutateAsync({productId});

        removeProductFromWishlistCache(productId);
        wishlistTracker('removed-from-wishlist');
        return UseToggleProductInWishlistResults.REMOVED;
      } else {
        addProductToWishlist.mutateAsync({
          productId,
          onError: (err?: string) => {
            onError?.(err);
            wishlistTracker('removed-from-wishlist');
          },
          onSuccess: onAddSuccess,
        });
        await yieldToMain();

        wishlistTracker('add-to-wishlist');
        return UseToggleProductInWishlistResults.ADDED;
      }
    },
    [
      addProductToWishlist,
      getProductIsInWishlist,
      onError,
      removeProductFromWishlist,
      removeProductFromWishlistCache,
      wishlistTracker,
    ],
  );
};

export const useAddProductToWishlistMutation = (onSuccess?: VoidFunction) => {
  const wishlistState = useWishlistMinimalState();
  const fullSession = useFullSession();
  const reductionsState = useReductionsStateRequestParameter();
  const targetGroupIdentifier = useTargetGroupIdentifier();
  const mutationCallback = useGrpcRequestMutation(
    WishlistService_AddProductLazy,
    {
      disabled: !fullSession,
      onSuccess,
    },
  );
  const shopId = useShopId();

  const logWishlistEvent = useCallback(
    ({
      productId,
      endAdd2WishlistSpan,
      success,
      code,
      message,
    }: {
      productId: number;
      endAdd2WishlistSpan: () => number;
      success: boolean;
      code: number;
      message?: string;
    }) => {
      Log.compare({
        actionName: Add2WishlistEvent,
        success,
        attributes: {
          code,
          productId,
          variantId: shopId,
          duration: endAdd2WishlistSpan(),
          message,
          sId:
            fullSession?.type?.$case === 'customer'
              ? fullSession.type.customer.authToken
              : fullSession?.type?.guest.sessionId || '',
        },
      });
    },
    [fullSession, shopId],
  );

  return {
    mutateAsync: ({
      productId,
      onError,
      onSuccess,
    }: {
      productId: number;
      onError?: (err?: string) => void;
      onSuccess?: (productId: number) => void;
    }) => {
      if (wishlistState.data) {
        onSuccess?.(productId);
        wishlistState.updateCache({
          ...wishlistState.data,
          wishlistState: {
            ...wishlistState.data.wishlistState,
            items: [
              ...(wishlistState.data.wishlistState?.items ?? []),
              {productId},
            ],
          },
        });
      }

      MUTATION_QUEUE.addToQueue({
        mutation: async (passedWishlistState: WishlistState | undefined) => {
          const currentWishlistStateData = passedWishlistState?.token
            ? passedWishlistState
            : wishlistState.data?.wishlistState;

          const removeOptimisticlyAddedProduct = () => {
            if (wishlistState.data && currentWishlistStateData) {
              wishlistState.updateCache({
                ...wishlistState.data,
                wishlistState: {
                  ...currentWishlistStateData,
                  items:
                    currentWishlistStateData.items.filter(
                      item => item.productId !== productId,
                    ) ?? [],
                },
              });
            }
          };

          const endAdd2WishlistSpan = startSpan();
          try {
            const response = await mutationCallback.mutateAsync({
              productId,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              session: fullSession!,
              reductionsState,
              wishlistToken: currentWishlistStateData?.token || null,
              targetGroup: targetGroupIdentifier,
            });

            if (!response.status.type) {
              logWishlistEvent({
                productId,
                endAdd2WishlistSpan,
                success: true,
                code: 200,
              });
            } else if (response.status.type.$case === 'error') {
              removeOptimisticlyAddedProduct();
              logWishlistEvent({
                productId,
                endAdd2WishlistSpan,
                success: false,
                code: -1,
                message: response.status.type.error.message,
              });

              onError?.();
            } else {
              removeOptimisticlyAddedProduct();
              logWishlistEvent({
                productId,
                endAdd2WishlistSpan,
                success: false,
                code: -1,
                message: response.status.type.wishlistFullError.message,
              });

              onError?.(response.status.type.wishlistFullError.message);
            }

            return response.wishlistState;
          } catch (err) {
            const error = err as GrpcResponseError;
            removeOptimisticlyAddedProduct();
            onError?.();
            logWishlistEvent({
              productId,
              endAdd2WishlistSpan,
              success: false,
              code: error.code || 0,
              message: error.message,
            });
          }
        },
        onLastItem: (response: WishlistState) => {
          if (response) {
            wishlistState.updateCache({
              ...wishlistState.data,
              wishlistState: response,
            });
          }
        },
        productId,
        actionType: 'add',
      });
    },
  };
};

export const useRemoveProductFromWishlistMutation = (
  setWishlistTokenState?: (token: string) => void,
) => {
  const {data: wishlistMinimalState, updateCache} = useWishlistMinimalState();
  const fullSession = useFullSession();
  const reductionsState = useReductionsStateRequestParameter();
  const targetGroupIdentifier = useTargetGroupIdentifier();
  const shopId = useShopId();
  const mutationCallback = useGrpcRequestMutation(
    WishlistService_RemoveProductLazy,
    {
      disabled: !fullSession,
    },
  );

  return {
    reset: mutationCallback.reset,
    mutateAsync: ({productId}: {productId: number}) => {
      // optimistic removal, helps with slow networks and large wishlists
      if (wishlistMinimalState) {
        updateCache({
          ...wishlistMinimalState,
          wishlistState: {
            ...wishlistMinimalState.wishlistState,
            items:
              wishlistMinimalState.wishlistState.items.filter(
                item => item.productId !== productId,
              ) ?? [],
          },
        });
      }

      const {promise, resolve, reject} = promiseWithResolver<
        WishlistState | undefined
      >();

      MUTATION_QUEUE.addToQueue({
        mutation: async (wishlistState: WishlistState | undefined) => {
          const endAdd2WishlistSpan = startSpan();

          try {
            const response = await mutationCallback.mutateAsync({
              productId,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              session: fullSession!,
              reductionsState,
              wishlistToken:
                wishlistState?.token ||
                wishlistMinimalState?.wishlistState.token ||
                null,
              targetGroup: targetGroupIdentifier,
            });
            Log.compare({
              actionName: RemoveFromWishlistEvent,
              success: true,
              attributes: {
                code: 200,
                productId,
                variantId: shopId,
                duration: endAdd2WishlistSpan(),
                sId:
                  fullSession?.type?.$case === 'customer'
                    ? fullSession.type.customer.authToken
                    : fullSession?.type?.guest.sessionId || '',
              },
            });

            resolve(response.wishlistState);
            return response.wishlistState;
          } catch (err) {
            const error = err as GrpcResponseError;
            Log.compare({
              actionName: RemoveFromWishlistEvent,
              success: false,
              attributes: {
                code: error.code || 0,
                productId,
                variantId: shopId,
                duration: endAdd2WishlistSpan(),
                message: error.message,
                sId:
                  fullSession?.type?.$case === 'customer'
                    ? fullSession.type.customer.authToken
                    : fullSession?.type?.guest.sessionId || '',
              },
            });

            reject(error);
          }
        },
        onLastItem: (response: WishlistState) => {
          if (response) {
            if (response.items.length === 0) {
              setWishlistTokenState?.(response.token.hash.toString());
            }
            updateCache({
              ...wishlistMinimalState,
              wishlistState: {...response},
            });
          }
        },
        productId,
        actionType: 'remove',
      });

      return promise;
    },
  };
};
